import { Injectable, inject } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Observable, catchError, of, switchMap, throwError, EMPTY, merge } from 'rxjs';
import { IndexedDBService } from '@libs/shared-services';
import { perioExamsDBKeys, SKIP_CACHE } from '@libs/utils';
import { PerioExam } from '@ffd/api-client';

export interface PerioExamsData {
  perioExams: PerioExam[];
  createdAt: Date;
}

@Injectable({ providedIn: 'root' })
export class PerioExamsInterceptorService implements HttpInterceptor {
  private readonly indexedDbService = inject(IndexedDBService);

  private db: IDBDatabase | undefined;

  constructor() {
    this.indexedDbService.dbOpen(perioExamsDBKeys.dbName, perioExamsDBKeys.version, perioExamsDBKeys.objectStoreName).subscribe((db) => {
      this.db = db;
    });
  }

  public intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<PerioExam[]>> {
    if (req.url.includes('/perio-exams') && this.db && req.method === 'GET') {
      const url = req.urlWithParams;
      const siteIdMatch = url.match(/sites\/([^/]+)/);
      const siteId = siteIdMatch ? siteIdMatch[1] : null;
      const patientIdMatch = url.match(/patients\/([^/]+)/);
      const patientId = patientIdMatch ? patientIdMatch[1] : null;
      const urlParams = new URLSearchParams(req.urlWithParams.split('?')[1]);
      const lastExamsCount = urlParams.get('lastExamCount');
      const perioExamsResponseId = `${patientId}_${siteId}_lastExamsCount=${lastExamsCount}`;

      if (perioExamsResponseId) {
        const shouldSkipCache = req.context.get(SKIP_CACHE);

        if (shouldSkipCache) {
          // Skip reading from cache but still update it with fresh data
          return this.handleRequestAndStoreInCache(perioExamsResponseId, next, req);
        }

        // Normal flow: return cached data first, then fresh data
        return merge(
          this.getCachedPerioExams(perioExamsResponseId).pipe(
            catchError((error) => {
              return EMPTY;
            }),
          ),
          this.handleRequestAndStoreInCache(perioExamsResponseId, next, req),
        );
      }
    }
    return next.handle(req);
  }

  private readonly getCachedPerioExams = (perioExamsResponseId: string) => {
    const readOS = this.db!.transaction([perioExamsDBKeys.objectStoreName], 'readonly').objectStore(perioExamsDBKeys.objectStoreName);
    return this.indexedDbService.dbGet<PerioExamsData>(readOS, perioExamsResponseId).pipe(
      switchMap((data) => {
        if (data) {
          return of(
            new HttpResponse({
              body: data.perioExams,
              status: 200,
            }),
          );
        }
        return EMPTY;
      }),
    );
  };

  private readonly handleRequestAndStoreInCache = (
    perioExamsResponseId: string,
    next: HttpHandler,
    req: HttpRequest<unknown>,
  ): Observable<HttpEvent<PerioExam[]>> => {
    return next.handle(req).pipe(
      catchError(() => {
        return throwError(() => 'Error fetching perio exams');
      }),
      switchMap((response) => {
        if (response instanceof HttpResponse) {
          const writeOs = this.db!.transaction([perioExamsDBKeys.objectStoreName], 'readwrite').objectStore(
            perioExamsDBKeys.objectStoreName,
          );
          return this.indexedDbService
            .dbPut<PerioExamsData>(writeOs, perioExamsResponseId, {
              perioExams: response.body,
              createdAt: new Date(),
            })
            .pipe(
              switchMap(async () => {
                await this.indexedDbService.dbHouseKeeping(writeOs);
                return response;
              }),
            );
        }
        return of(response);
      }),
    );
  };
}
